<template>
  <div class="chatbot-container">
    <div class="chat-header">
      <h1>CPD Chatbot</h1>
      <span v-if="recordsUsed" class="records-badge">Used {{ recordsUsed }} records from {{ dataSource }} data</span>
      <!-- <span v-if="dataSource" class="records-badge">{{ dataSource }}</span> -->
    </div>

    <div class="chat-messages" ref="messagesContainer">
      <div
        v-for="(message, index) in messages"
        :key="index"
        :class="['message', message.role]"
      >
        <div class="message-content">
          <strong>{{ message.role === 'user' ? 'You' : 'AI' }}:</strong>
          <p>{{ message.content }}</p>
        </div>
      </div>

      <div v-if="loading" class="message assistant">
        <div class="message-content">
          <strong>AI:</strong>
          <p class="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </p>
        </div>
      </div>

      <div v-if="error" class="error-message">
        <strong>Error:</strong> {{ error }}
      </div>
    </div>

    <div class="chat-input">
      <input
        v-model="userInput"
        @keyup.enter="sendMessage"
        :disabled="loading"
        placeholder="Ask a question..."
        type="text"
      />
      <button @click="sendMessage" :disabled="loading || !userInput.trim()">
        {{ loading ? 'Sending...' : 'Send' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatbotUI',
  
  props: {
    apiEndpoint: {
      type: String,
      required: true,
    },
    filter: {
      type: Object,
      required: true,
    }
  },

  data() {
    return {
      messages: [],
      userInput: '',
      loading: false,
      error: null,
      recordsUsed: null,
      dataSource: null
    };
  },

  methods: {
    async sendMessage() {
      if (!this.userInput.trim() || this.loading) return;

      const query = this.userInput.trim();
      this.userInput = '';
      this.error = null;

      // Add user message
      this.messages.push({
        role: 'user',
        content: query
      });

      this.loading = true;
      this.scrollToBottom();

      try {
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            query: query,
            filter: this.filter
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Add assistant response
        this.messages.push({
          role: 'assistant',
          content: data.answer
        });

        this.recordsUsed = data.recordsUsed;
        this.dataSource = data.dataSource;

      } catch (err) {
        console.error('Error sending message:', err);
        this.error = err.message || 'Failed to get response from chatbot';
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    clearChat() {
      this.messages = [];
      this.error = null;
      this.recordsUsed = null;
      this.dataSource = null;
    }
  }
};
</script>

<style scoped>
.chatbot-container {
  display: flex;
  flex-direction: column;
  /* height: 600px; */
  height: 800px;
  max-width: 80%;
  padding: 3rem 1.5rem;
  margin: 0 auto;
  /* border: 1px solid #ddd; */
  /* border-radius: 8px; */
  background: #fff;
  /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); */
}

.chat-header {
  padding: 0 0 16px 0;
  background: white;
  color: #4a4a4a;
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-header h1 {
  margin: 0;
  /* font-size: 100%; */
  font-size: 26px;
  font-weight: 400;
  line-height: 1.5;
  font-family: Heebo, Helvetica, Arial, sans-serif;
}

.records-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f7fafc;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.message.user {
  justify-content: flex-end;
}

.message.assistant {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 8px;
  line-height: 1.5;
}

.user .message-content {
  /* background: #4299e1; */
  /* background: #f78f20; */
  background: #527fff;
  color: white;
}

.assistant .message-content {
  background: #e2e8f0;
  color: #2d3748;
}

.message-content strong {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  opacity: 0.8;
}

.message-content p {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #718096;
  border-radius: 50%;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

.error-message {
  padding: 12px;
  background: #fed7d7;
  color: #c53030;
  border-radius: 8px;
  margin-bottom: 16px;
}

.chat-input {
  display: flex;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: white;
  border-radius: 0 0 8px 8px;
}

.chat-input input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.chat-input input:focus {
  border-color: #1c6faf;
}

.chat-input input:disabled {
  background: #f7fafc;
  cursor: not-allowed;
}

.chat-input button {
  margin-left: 12px;
  padding: 12px 24px;
  background: #1c6faf;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.chat-input button:hover:not(:disabled) {
  background: #3182ce;
}

.chat-input button:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
}
</style>